/* eslint-disable react/no-children-prop */
/* eslint-disable react/prop-types */
import React from 'react';

import { Header, Grid, Label, Button, Breadcrumb, Card } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import {
  SEO,
  Image,
  MarkdownContent,
  TeamMemberV2,
  OutboundLink,
  InternalLink,
  BackgroundImageTeamMember,
  StaffMemberSchema,
  BreadcrumbListSchema,
  WebsiteComponentInteraction,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  EVENT_LABELS,
  Email,
  Phone,
  FadeReveal,
  BasicCarousel,
  componentIterator,
  SlideReveal,
  multipleComponentIterator,
  useResponsive,
  PriceRange,
} from '@bluefin/components';

import { createServiceItemSlug, createTeamMemberSlug } from '../../index';
import {
  getPageByType,
  getBookNowPage,
  getCtaInteractions,
  getBusinessFiles,
  getTeamMemberServices,
  getComponentContentNodeContent,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes, getPageUrl } from '../../utils';

export default function TeamMemberTemplate({ data, pageContext }) {
  const { isDesktop } = useResponsive();
  const {
    fishermanBusinessTeam: teamMember = {},
    fishermanBusiness: { name: businessName, businessCategories = [], website },
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    fishermanBusinessWebsitePage: { components: contentNodeComponents = [] } = {},
    allFishermanBusinessTeam: { nodes: allTeamMembers = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] } = {},
    allFishermanBusinessMenuCategory: { nodes: allCategories = [] } = {},
    allFishermanBusinessMenuItem: { nodes: allItems = [] } = {},
    site: {
      siteMetadata: { baseUrl },
    },
  } = data;

  const {
    fishermanParentPageTitle = 'Team',
    fishermanParentPageSlug = '/team/',
    templateProps = {},
  } = pageContext;
  const { otherTeamMembersText = 'Other Team Members', priceRangeProps = {} } = templateProps;

  const {
    _id,
    name,
    email,
    phone,
    description,
    role,
    gatsbyImage,
    image,
    socialMedia,
    locations = [],
    interactions = [],
    hours = [],
    files = [],
    associatedItemVariations = [],
    teams = [],
    seoTitle,
    seoDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
  } = teamMember;

  const url = getPageUrl(website, [
    createTeamMemberSlug(teamMember, allTeamMembers, fishermanParentPageSlug),
  ]);

  function getOtherTeamMembers() {
    return allTeamMembers.filter(({ _id: otherTeamMemberId }) => otherTeamMemberId !== _id);
  }

  const bookingPage = getBookNowPage(allPages);
  const teamPage = getPageByType(allPages, 'Team');
  const otherTeamMembers = getOtherTeamMembers();
  const { title: teamPageTitle, slug: teamPageSlug } = teamPage || {};
  const filteredInteractions = getCtaInteractions(interactions);

  const headshot = gatsbyImage || image;
  const hasHeadshot = !_isNil(headshot);
  let teamMemberBusinessFiles = getBusinessFiles(files, allFiles);
  if (hasHeadshot) {
    const headShotBusinessFile = { _id: 0, src: headshot };
    teamMemberBusinessFiles = [headShotBusinessFile, ...teamMemberBusinessFiles];
  }
  const hasImages = !_isEmpty(teamMemberBusinessFiles);

  const teamMemberServices = getTeamMemberServices(allCategories, associatedItemVariations);

  const otherTeamMembersHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'other_team_members_header',
  });
  const seeAllTeamButtonContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'see_all_team_button',
  });

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={`${fishermanParentPageTitle} - ${name}`}
        title={seoTitle}
        description={seoDescription || description}
        truncateDescription
        images={hasHeadshot ? [image] : null}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        ogUrl={url}
      />
      <StaffMemberSchema
        businessName={businessName}
        businessCategories={businessCategories}
        name={name}
        jobTitle={role}
        description={description}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, ensureSlugSlashes(fishermanParentPageSlug)),
          },
          {
            name: `${fishermanParentPageTitle} - ${name} | ${businessName}`,
          },
        ]}
      />
      <div className="team-member-page-container">
        <Grid stackable className="team-member-header-container">
          <Grid.Column width={8} className="content-column">
            <Breadcrumb>
              <Breadcrumb.Section
                link
                to={ensureSlugSlashes(fishermanParentPageSlug)}
                as={InternalLink}
              >
                {fishermanParentPageTitle}
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon="right chevron" />
              <Breadcrumb.Section>{name}</Breadcrumb.Section>
            </Breadcrumb>
            <Header as="h1">{name}</Header>
            <Header>{role}</Header>

            <div className="icons">
              {!_isEmpty(email) && <Email as="icon" email={email} />}
              {!_isEmpty(phone) && (
                <Phone phone={phone} iconName="phone" ariaLabel={`Call ${name}`} />
              )}
            </div>

            {!_isEmpty(socialMedia) && (
              <TeamMemberV2.Social className="team-member-social" social={socialMedia} showHandle />
            )}

            {!_isEmpty(teams) && (
              <div className="teams">
                {teams.map(({ _id: teamId, name: teamName }) => (
                  <Label key={teamId} className="label" content={teamName} />
                ))}
              </div>
            )}

            {!_isEmpty(locations) && (
              <div className="team-member-locations">
                {locations.map(({ _id: id, name: locationName }) => (
                  <Label key={id}>{locationName}</Label>
                ))}
              </div>
            )}

            <MarkdownContent className="description">{description}</MarkdownContent>

            <TeamMemberV2.Schedule hours={hours} fullDayName />

            <div className="team-member-actions">
              {bookingPage && _isEmpty(filteredInteractions) && (
                <Button
                  primary
                  as={OutboundLink}
                  to={bookingPage.url}
                  event={{
                    category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                  }}
                >
                  {bookingPage.title}
                </Button>
              )}
              {filteredInteractions.map((interaction) => {
                const { _id: id } = interaction;
                return (
                  <WebsiteComponentInteraction
                    key={id}
                    interaction={interaction}
                    className="team-member-interaction"
                    displayComponentPropsMap={{
                      button: {
                        size: null,
                        compact: false,
                      },
                    }}
                    event={{
                      category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.TEAM_MEMBER_INTERACTION_ACTION,
                    }}
                  />
                );
              })}
            </div>
          </Grid.Column>
          {hasImages && (
            <Grid.Column width={8} className="image-column">
              <FadeReveal triggerOnce>
                <BasicCarousel
                  className="service-object-images"
                  size={1}
                  grouping={1}
                  displayIndicators
                  displayPlayPauseButton={false}
                  autoScroll={false}
                  animation={{
                    name: 'blur',
                    duration: 200,
                    transitionProps: {
                      preAnimating: { opacity: 0.99, blur: '2px' },
                      animating: { opacity: 0.99, blur: '2px' },
                    },
                  }}
                  items={componentIterator({
                    iterator: teamMemberBusinessFiles,
                    component: (
                      <Image background className="team-member-image-item" loadStrategy="eager" />
                    ),
                    propMap: { src: 'src', altText: 'altText' },
                  })}
                />
              </FadeReveal>

              {!_isEmpty(teamMemberServices) && (
                <div className="team-member-services-list">
                  {teamMemberServices.map(({ name: categoryName, items }) => {
                    return (
                      <div className="team-member-service-category" key={categoryName}>
                        <Header as="h4">{categoryName}</Header>
                        {items.map((item) => {
                          const { name: itemName, variations } = item;

                          return (
                            <InternalLink
                              className="team-member-service-item"
                              key={itemName}
                              to={createServiceItemSlug(item, allItems, allCategories)}
                            >
                              <Header as="h5">{itemName}</Header>
                              <PriceRange
                                prices={variations.map(({ price }) => ({ amount: price }))}
                                settings={{
                                  allowMixedFormats: true,
                                  displayZeroCost: false,
                                  displayType: 'range',
                                  hideCurrencySign: true,
                                }}
                                {...(priceRangeProps || {})}
                              />
                            </InternalLink>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </Grid.Column>
          )}
        </Grid>
        {!_isEmpty(otherTeamMembers) && (
          <Grid stackable textAlign="center" className="team-member-other-stylists-container">
            <Grid.Column width={16} textAlign="center">
              <Header as="h2">{otherTeamMembersHeaderContent || otherTeamMembersText}</Header>
              <Card.Group itemsPerRow={3} className="stylists">
                <SlideReveal
                  cascade
                  triggerOnce
                  className="ui card"
                  fade
                  direction="up"
                  children={multipleComponentIterator({
                    iterator: otherTeamMembers,
                    limit: isDesktop ? 3 : 4,
                    components: [
                      {
                        component: <BackgroundImageTeamMember useModal={false} as={InternalLink} />,
                        propMap: {
                          key: '_id',
                          'teamMember.name': 'name',
                          'teamMember.role': 'role',
                          'teamMember.photo': 'gatsbyImage',
                          to: createTeamMemberSlug,
                        },
                      },
                    ],
                  })}
                />
              </Card.Group>
              {teamPage && (
                <Button
                  to={ensureSlugSlashes(teamPageSlug)}
                  primary
                  as={InternalLink}
                  event={{
                    category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.TEAM_VIEW_ALL_MEMBERS_EVENT_ACTION,
                    label: EVENT_LABELS.TEAM_MEMBER_LABEL,
                  }}
                >
                  {seeAllTeamButtonContent || `See all ${teamPageTitle}`}
                </Button>
              )}
            </Grid.Column>
          </Grid>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: Int!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    fishermanBusinessTeam(_id: { eq: $id }) {
      _id
      name
      email
      phone
      description
      role
      image
      gatsbyImage {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      socialMedia {
        platform
        url
      }
      locations {
        _id
        name
      }
      hours {
        startDay
        openTime
        closeTime
      }
      interactions {
        behaviorType
        displayType
        enabled
        label
        url
        style
      }
      files
      associatedItemVariations {
        itemId
        variationId
        price
        item {
          _id
          name
          variations {
            _id
            name
            order
            price
          }
          visible
        }
      }
      teams {
        _id
        name
      }
      seoTitle
      seoDescription
      seoOgTitle
      seoOgDescription
      seoOgImage {
        url
        alt
        type
      }
    }
    fishermanBusiness {
      name
      businessCategories {
        category
      }
      website {
        domains {
          url
          primaryDomain
        }
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
        slug
      }
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Team" }) {
      components {
        componentIdentifier
        contentNodes {
          content
        }
      }
    }
    allFishermanBusinessTeam(sort: { order: ASC, fields: order }, filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        role
        image
        gatsbyImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        show
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessMenuCategory(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        items
      }
    }
    allFishermanBusinessMenuItem {
      nodes {
        _id
        name
      }
    }
  }
`;
